import React, { useEffect, useState, useCallback, useMemo } from "react";
import { generateInvoicePDF } from "../../../utils/invoiceUtils.js";
import {
  Drawer,
  Space,
  Button,
  message,
  Tag,
  Spin,
  Modal,
  List,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useSettings } from "../../../contexts/settingsContext.js";
import {
  getInvoicesByContestant,
  getInvoiceByIDNumber,
  toggleInvoicePaidStatus,
  markGroupMembersInvoicesPaid,
} from "../../../api/invoice";
import {
  fetchReceiptsByContestantId,
  fetchReceiptsByGroupId,
} from "../../../api/receipt";
import {
  getApplicationsByContestant,
  getApplicationsByGroup,
  deleteApplication,
} from "../../../api/application";
import { getInvoicesByGroup } from "../../../api/group.js";
import {
  downloadCertificate,
  emailReceiptCopy,
} from "../../../api/competition.js";
import PaymentButton from "../invoiceButton.js";
import InvoiceStatisticsComponent from "./invoiceStatistics.js";
import InvoiceDetailsComponent from "./invoiceDetails.js";
import { loadState } from "../../../stores/store.js";
import "./invoiceDrawer.scss";

const InvoiceStatistics = React.memo(InvoiceStatisticsComponent);
const InvoiceDetails = React.memo(InvoiceDetailsComponent);

const InvoiceDrawer = ({
  contestant,
  group,
  source,
  onRequestClose,
  fetchData,
}) => {
  const { state: settings } = useSettings();
  const state = loadState();

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [applications, setApplications] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);

  const [selectedGroupMember, setSelectedGroupMember] = useState(null);
  const [groupMemberInvoice, setGroupMemberInvoice] = useState(null);
  const [groupMemberLoading, setGroupMemberLoading] = useState(false);

  const [bulkMarkingPaid, setBulkMarkingPaid] = useState(false);
  const [allGroupMemberInvoices, setAllGroupMemberInvoices] = useState([]);

  const entity = source === "contestant" ? contestant : group;
  const entityId = entity?._id;

  const recipientInfo = useMemo(() => {
    if (source === "contestant" && contestant) {
      return {
        name: `${contestant.first_name} ${contestant.last_name}`,
        email: contestant.email,
        province: contestant.province,
        number: contestant.contestant_number,
        source: "contestant",
        round: contestant.round,
      };
    }
    if (source === "group" && group) {
      return {
        name: group.group_name || "Group",
        email: group.teacher_email || "",
        province: group.province,
        number: group.group_number,
        source: "group",
        round: group.round,
      };
    }
    return {
      name: "",
      email: "",
      province: null,
      number: "",
      source: "",
      round: null,
    };
  }, [source, contestant, group]);

  const loadInvoiceAndApplications = useCallback(async () => {
    if (!entityId) return;

    setLoading(true);
    try {
      if (source === "contestant" && contestant) {
        const idVal = contestant.id_number;
        const passportVal = contestant.passport_number;

        const hasValidIdNumber =
          idVal &&
          typeof idVal === "string" &&
          idVal.trim() !== "" &&
          idVal.trim() !== "0" &&
          idVal.trim().toLowerCase() !== "no";

        const idToSend = hasValidIdNumber ? idVal : passportVal;

        const [invoiceData, applicationsData] = await Promise.all([
          getInvoicesByContestant(entityId),
          getApplicationsByContestant(entityId, idToSend),
        ]);

        if (!invoiceData || invoiceData.length === 0) {
          setInvoices([]);
          setSelectedInvoice(null);
        } else {
          const selectedInv =
            invoiceData.find(
              (invoice) => invoice.roundNumber === contestant.round
            ) || invoiceData[0];
          setInvoices(invoiceData);
          setSelectedInvoice(selectedInv);
        }

        const roundApps = (applicationsData || []).filter(
          (application) => application.round === contestant.round
        );
        setApplications(roundApps);
      } else if (source === "group" && group) {
        const [invoiceData, applicationsData] = await Promise.all([
          getInvoicesByGroup(entityId),
          getApplicationsByGroup(entityId),
        ]);

        if (!invoiceData || invoiceData.length === 0) {
          setInvoices([]);
          setSelectedInvoice(null);
        } else {
          const selectedInv =
            invoiceData.find(
              (invoice) => invoice.roundNumber === group.round
            ) || invoiceData[0];
          setInvoices(invoiceData);
          setSelectedInvoice(selectedInv);
        }

        const roundApps = (applicationsData || []).filter(
          (application) => application.round === group.round
        );
        setApplications(roundApps);
      } else {
        setInvoices([]);
        setSelectedInvoice(null);
        setApplications([]);
      }
    } catch (error) {
      console.error("Error loading invoice data", error);
      message.error("Error loading invoice data");
    } finally {
      setLoading(false);
    }
  }, [entityId, source, contestant, group]);

  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        if (source === "contestant" && contestant?._id) {
          const data = await fetchReceiptsByContestantId(contestant._id);
          setReceipts(data || []);
        } else if (source === "group" && group?._id) {
          const data = await fetchReceiptsByGroupId(group._id);
          setReceipts(data || []);
        } else {
          setReceipts([]);
        }
      } catch (error) {
        console.error("Failed to fetch receipts:", error);
      }
    };

    fetchReceipts();
  }, [source, contestant?._id, group?._id]);

  useEffect(() => {
    if (entityId) {
      loadInvoiceAndApplications();
    }
  }, [entityId, loadInvoiceAndApplications]);

  const isRound2Group = useMemo(
    () =>
      source === "group" &&
      group &&
      (group.round === 2 || settings.selectedRound === 2),
    [source, group, settings.selectedRound]
  );

  const groupMembers = useMemo(
    () =>
      isRound2Group && group && Array.isArray(group.group) ? group.group : [],
    [isRound2Group, group]
  );

  const unpaidGroupInvoices = useMemo(
    () =>
      isRound2Group ? (invoices || []).filter((inv) => !inv.paid) : [],
    [isRound2Group, invoices]
  );

  useEffect(() => {
    const loadAllMemberInvoices = async () => {
      if (!isRound2Group || !group || groupMembers.length === 0) {
        setAllGroupMemberInvoices([]);
        return;
      }

      try {
        const results = await Promise.all(
          groupMembers
            .filter((member) => member.id_number)
            .map((member) => getInvoiceByIDNumber(member.id_number, group.round))
        );

        const allInvoices = results
          .filter(Boolean)
          .flat()
          .filter(
            (inv) => inv && !inv.paid && inv.roundNumber === group.round
          );

        setAllGroupMemberInvoices(allInvoices);
      } catch (err) {
        console.error(
          "[InvoiceDrawer] Error loading group member invoices for Pay All:",
          err
        );
        setAllGroupMemberInvoices([]);
      }
    };

    loadAllMemberInvoices();
  }, [isRound2Group, group, groupMembers]);

  useEffect(() => {
    const fetchMemberInvoice = async () => {
      if (isRound2Group && selectedGroupMember?.id_number) {
        setGroupMemberLoading(true);
        try {
          const invoice = await getInvoiceByIDNumber(
            selectedGroupMember.id_number,
            group.round
          );
          setGroupMemberInvoice(invoice?.[0] || null);
        } catch (e) {
          setGroupMemberInvoice(null);
        } finally {
          setGroupMemberLoading(false);
        }
      } else {
        setGroupMemberInvoice(null);
      }
    };

    fetchMemberInvoice();
  }, [selectedGroupMember, isRound2Group, group?.round]);

  const currentInvoice =
    isRound2Group && selectedGroupMember ? groupMemberInvoice : selectedInvoice;

  const invoiceApplicationIds = useMemo(
    () => currentInvoice?.applications?.map((app) => app.application._id) || [],
    [currentInvoice]
  );

  const filteredApplications = useMemo(
    () => applications.filter((app) => invoiceApplicationIds.includes(app._id)),
    [applications, invoiceApplicationIds]
  );

  const handleGeneratePDF = useCallback(() => {
    if (!currentInvoice) return;

    const invoiceData = {
      contestantName: recipientInfo.name,
      totalEvents: currentInvoice.applications?.length || 0,
      feeBreakdown: currentInvoice.fee || {},
      applications: filteredApplications || [],
      passes: currentInvoice.passes || [],
      merchandise: currentInvoice.merchandise || [],
    };

    generateInvoicePDF(invoiceData, settings, filteredApplications);
  }, [currentInvoice, filteredApplications, recipientInfo, settings]);

  const handleRemoveApplication = useCallback(
    async (applicationId) => {
      if (currentInvoice?.paid) {
        message.error("Cannot remove application from a paid invoice");
        return;
      }
      if (!entityId) {
        message.error("No entity data available.");
        return;
      }
      try {
        await deleteApplication(applicationId);
        message.success("Application removed successfully");
        await loadInvoiceAndApplications();
      } catch (error) {
        console.error("Error removing application", error);
        message.error("Error removing application");
      }
    },
    [currentInvoice?.paid, entityId, loadInvoiceAndApplications]
  );

  const handleSendEmailCopy = async (receipt) => {
    try {
      let receiptPayload;

      if (receipt.group) {
        const { profilePhotos, ...groupWithoutPhotos } = receipt.group || {};
        receiptPayload = {
          ...receipt,
          group: groupWithoutPhotos,
        };
        delete receiptPayload.contestant;
      } else if (receipt.contestant) {
        const { profilePhoto, ...contestantWithoutPhoto } =
          receipt.contestant || {};
        receiptPayload = {
          ...receipt,
          contestant: contestantWithoutPhoto,
        };
        delete receiptPayload.group;
      } else {
        receiptPayload = { ...receipt };
      }

      await emailReceiptCopy(receiptPayload);
      message.success("Email copy sent successfully!");
    } catch (err) {
      console.error("[InvoiceDrawer] Error in handleSendEmailCopy:", err);
      message.error("Failed to send email copy.");
    }
  };

  const handleMarkAsPaid = useCallback(
    async () => {
      if (!currentInvoice || !currentInvoice._id) {
        message.error("No invoice selected.");
        return;
      }
      if (!entityId) {
        message.error("No entity data available.");
        return;
      }

      setLoading(true);

      try {
        const receiptName =
          recipientInfo.source === "group" &&
          isRound2Group &&
          selectedGroupMember?.name
            ? selectedGroupMember.name
            : recipientInfo.name;

        await toggleInvoicePaidStatus(currentInvoice._id, true);
        message.success("Invoice marked as paid.");

        await loadInvoiceAndApplications();
        if (typeof fetchData === "function") {
          fetchData();
        }

        // The receipt email is now sent automatically server-side when the
        // invoice is marked paid (toggleInvoicePaidStatus). The client only
        // handles the round 2 certificate below.
        if (recipientInfo.round === 2 && recipientInfo.email) {
          await downloadCertificate(
            receiptName,
            settings._id,
            recipientInfo.email
          );
        }
      } catch (error) {
        console.error(error);
        message.error("Error marking invoice as paid.");
      } finally {
        setLoading(false);
      }
    },
    [
      currentInvoice,
      entityId,
      loadInvoiceAndApplications,
      fetchData,
      recipientInfo,
      group,
      settings._id,
      isRound2Group,
      selectedGroupMember,
    ]
  );

  const handleMarkAllGroupMembersPaid = useCallback(
    async () => {
      if (!isRound2Group) {
        message.error("Bulk marking is only available for round 2 groups.");
        return;
      }

      if (!group || !groupMembers || groupMembers.length === 0) {
        message.error("No group members found.");
        return;
      }

      const memberIdNumbers = groupMembers
        .map((member) => member.id_number)
        .filter(Boolean);

      if (memberIdNumbers.length === 0) {
        message.error("No valid ID numbers for group members.");
        return;
      }

      const emailAddress = group.teacher_email;

      setBulkMarkingPaid(true);

      try {
        await markGroupMembersInvoicesPaid(memberIdNumbers);

        // Receipt emails are now sent automatically server-side when each
        // member invoice is marked paid. The client only handles the round 2
        // certificate per member below.
        if (emailAddress && group.round === 2) {
          for (const member of groupMembers) {
            const memberName = member.name || "";

            try {
              await downloadCertificate(memberName, settings._id, emailAddress);
            } catch (err) {
              console.error(
                "[InvoiceDrawer] Failed to send certificate for member:",
                { memberName, memberId: member.id_number },
                err
              );
            }
          }
        }

        await loadInvoiceAndApplications();
        if (typeof fetchData === "function") {
          fetchData();
        }

        message.success(
          "All group member invoices marked as paid and emails sent."
        );
      } catch (error) {
        console.error(
          "[InvoiceDrawer] Error in handleMarkAllGroupMembersPaid:",
          error
        );
        message.error("Error marking all group member invoices as paid.");
      } finally {
        setBulkMarkingPaid(false);
      }
    },
    [
      isRound2Group,
      group,
      groupMembers,
      selectedInvoice,
      settings._id,
      loadInvoiceAndApplications,
      fetchData,
    ]
  );

  const titleText = useMemo(() => {
    if (source === "contestant" && contestant) {
      return `Invoices for ${contestant.first_name} ${contestant.last_name}`;
    }
    if (source === "group" && group) {
      return `Invoices for ${group.group_name || "Group"}`;
    }
    return "Invoices";
  }, [source, contestant, group]);

  const handleReceiptClick = (receipt) => {
    Modal.info({
      title: "Receipt Details",
      content: (
        <div>
          <p>
            <strong>Amount Paid:</strong> R{receipt?.amount_paid}
          </p>
          <p>
            <strong>Date:</strong>{" "}
            {new Date(receipt?.createdAt).toLocaleDateString()}
          </p>
          <p>
            <strong>Applications:</strong>
          </p>
          <ul>
            {receipt?.applications?.map((app, idx) => {
              const appFromRef = app?.application;
              const appFromSnapshot = app?.application_snapshot;

              const key =
                (appFromRef && appFromRef._id) ||
                (appFromSnapshot && appFromSnapshot._id) ||
                app?.name ||
                idx;

              const name =
                (appFromRef && appFromRef.name) ||
                (appFromSnapshot && appFromSnapshot.name) ||
                app?.name ||
                "Unknown application";

              return <li key={key}>{name}</li>;
            })}
          </ul>
        </div>
      ),
      onOk() {},
    });
  };

  return (
    <Drawer
      closable
      destroyOnClose
      title={
        <div className="header-actions">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <span>{titleText}</span>
            {currentInvoice?.invoice_number && (
              <span style={{ fontSize: "0.9em", color: "#888" }}>
                Invoice Number: {currentInvoice.invoice_number}
              </span>
            )}
          </div>
          <Space>
            {currentInvoice && (
              <>
                {currentInvoice.paid && <Tag color="green">Paid</Tag>}
                {!currentInvoice.paid && (
                  <>
                    <PaymentButton
                      invoices={[currentInvoice]}
                      settings={settings}
                    />
                    {state?.user?.role === "admin" && (
                      <Button onClick={handleMarkAsPaid} disabled={loading}>
                        {loading ? <Spin size="small" /> : "Mark as Paid"}
                      </Button>
                    )}
                  </>
                )}
                <Button
                  className="generate-pdf-button"
                  type="primary"
                  onClick={handleGeneratePDF}
                  disabled={loading}
                >
                  <DownloadOutlined /> Generate Invoice PDF
                </Button>
                {receipts.length > 0 && (
                  <Button
                    type="primary"
                    style={{ width: "120px" }}
                    onClick={() => setModalVisible(true)}
                  >
                    View Receipts
                  </Button>
                )}
              </>
            )}
          </Space>
        </div>
      }
      open={true}
      placement="right"
      onClose={onRequestClose}
      width={800}
      bodyStyle={{ paddingBottom: "64px", paddingTop: "0px" }}
    >
      <div className="content-wrapper">
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          {isRound2Group ? (
            <>
              {!selectedGroupMember ? (
                <>
                  <h3>Select a group member</h3>

                  {group && group.paid === false && (
                    <Space style={{ marginBottom: 10 }}>
                      <PaymentButton
                        invoices={allGroupMemberInvoices}
                        settings={settings}
                        label="Pay All Invoices"
                      />

                      {state?.user?.role === "admin" && (
                        <Button
                          type="primary"
                          onClick={handleMarkAllGroupMembersPaid}
                          disabled={bulkMarkingPaid}
                        >
                          {bulkMarkingPaid ? (
                            <Spin size="small" />
                          ) : (
                            "Mark all as paid"
                          )}
                        </Button>
                      )}
                    </Space>
                  )}

                  <List
                    bordered
                    dataSource={groupMembers}
                    renderItem={(member) => (
                      <List.Item
                        key={member.id_number}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedGroupMember(member)}
                      >
                        <Space>{member.name}</Space>
                      </List.Item>
                    )}
                  />
                </>
              ) : (
                <>
                  <Button
                    onClick={() => setSelectedGroupMember(null)}
                    style={{ marginBottom: 10 }}
                  >
                    ← Back to group members
                  </Button>
                  {groupMemberLoading ? (
                    <Spin />
                  ) : groupMemberInvoice ? (
                    <>
                      <InvoiceStatistics
                        loading={loading}
                        invoice={groupMemberInvoice}
                      />
                      <InvoiceDetails
                        loading={loading}
                        invoice={groupMemberInvoice}
                        applications={
                          groupMemberInvoice?.applications?.map(
                            (a) => a.application
                          ) ?? []
                        }
                        handleRemoveApplication={handleRemoveApplication}
                      />
                    </>
                  ) : (
                    <p>No individual invoice found for this member.</p>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <InvoiceStatistics loading={loading} invoice={currentInvoice} />
              <InvoiceDetails
                loading={loading}
                invoice={currentInvoice}
                applications={filteredApplications}
                handleRemoveApplication={handleRemoveApplication}
              />
            </>
          )}
        </Space>
      </div>

      <Modal
        title="Receipts"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <List
          dataSource={receipts}
          renderItem={(receipt) => (
            <List.Item>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <span>
                  {`R${receipt.amount_paid} - ${new Date(
                    receipt.createdAt
                  ).toLocaleDateString()}`}
                </span>
                <div>
                  <Button
                    type="link"
                    onClick={() => handleReceiptClick(receipt)}
                  >
                    View Receipt
                  </Button>
                  {state?.user?.role === "admin" && (
                    <Button
                      type="link"
                      onClick={() => handleSendEmailCopy(receipt)}
                      style={{ marginLeft: 8 }}
                    >
                      Email Copy
                    </Button>
                  )}
                </div>
              </div>
            </List.Item>
          )}
        />
      </Modal>
    </Drawer>
  );
};

export default React.memo(InvoiceDrawer);
